import ControlPanelController from "./control_panel_controller"
import Sortable from "sortablejs"

/**
 * Stimulus controller for Construction sub-tab CRUD.
 * Used by daily_report_types, punchlist_kinds, categories,
 * activity_locations, cost_types, measure_units, user_labels.
 */
export default class extends ControlPanelController {
  static targets = [
    "searchInput",
    "createModal", "editModal", "deleteConfirmModal",
    "createName", "createParentId", "createSequence", "createCode", "createDescription", "createFormTemplateId", "createAllowHideAutoWeather",
    "createShowInDesign", "createShowInConstruction",
    "editId", "editName", "editSequence", "editCode", "editDescription", "editFormTemplateId", "editAllowHideAutoWeather",
    "editShowInDesign", "editShowInConstruction",
    "deleteId"
  ]

  static values = {
    createUrl: String,
    baseUrl: String,
    resourceKey: String,
    toggleUrl: String,
    setDefaultUrl: String,
    reorderUrl: String,
    toggleEditNumberUrl: String
  }

  connect() {
    super.connect()
    this._initSortable()
  }

  _initSortable() {
    if (!this.hasReorderUrlValue) return
    const tbody = this.element.querySelector("tbody")
    if (!tbody) return
    Sortable.create(tbody, {
      handle: ".drag-handle",
      animation: 150,
      ghostClass: "opacity-30",
      onEnd: () => this._onSortEnd()
    })
  }

  async _onSortEnd() {
    const rows = this.element.querySelectorAll("tbody tr")
    const sequences = []
    rows.forEach((row, index) => {
      const id = row.dataset.id
      const seq = index + 1
      sequences.push([id, seq])
      // Update the sequence badge inline
      const cells = row.querySelectorAll("td")
      const seqCell = cells[1] // drag-handle is cells[0], sequence is cells[1]
      if (seqCell) {
        const badge = seqCell.querySelector("span")
        if (badge) {
          badge.textContent = seq
        } else {
          seqCell.innerHTML = `<span class="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[13px] leading-none bg-gray-200 text-text-muted">${seq}</span>`
        }
      }
      // Update edit button data-sequence
      const editBtn = row.querySelector('[data-action*="openEditModal"]')
      if (editBtn) editBtn.dataset.sequence = seq
    })
    this._restripe()

    try {
      const response = await fetch(this.reorderUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ sequences: JSON.stringify(sequences) })
      })
      if (response.ok) {
        this._showToast("Order updated")
      } else {
        window.location.reload()
      }
    } catch {
      window.location.reload()
    }
  }

  // ── Search ────────────────────────────────────────────────────────

  search() {
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      const q = this.hasSearchInputTarget ? this.searchInputTarget.value.trim().toLowerCase() : ""
      const rows = this.element.querySelectorAll("tbody tr")
      rows.forEach(row => {
        const text = row.textContent.toLowerCase()
        row.style.display = !q || text.includes(q) ? "" : "none"
      })
    }, 200)
  }

  // ── Create ────────────────────────────────────────────────────────

  // New report types start in Construction only (Phelipe 2026-10-07).
  openCreateModal() {
    super.openCreateModal()
    if (this.hasCreateShowInDesignTarget) this.createShowInDesignTarget.checked = false
    if (this.hasCreateShowInConstructionTarget) this.createShowInConstructionTarget.checked = true
  }

  submitCreate() {
    const body = { name: this.createNameTarget.value.trim() }
    if (this.hasCreateParentIdTarget && this.createParentIdTarget.value) {
      body.parent_id = this.createParentIdTarget.value
    }
    if (this.hasCreateSequenceTarget && this.createSequenceTarget.value) {
      body.sequence = this.createSequenceTarget.value
    }
    if (this.hasCreateCodeTarget && this.createCodeTarget.value) {
      body.code = this.createCodeTarget.value
    }
    if (this.hasCreateDescriptionTarget) {
      body.description = this.createDescriptionTarget.value.trim()
    }
    if (this.hasCreateFormTemplateIdTarget && this.createFormTemplateIdTarget.value) {
      body.form_template_id = this.createFormTemplateIdTarget.value
    }
    if (this.hasCreateAllowHideAutoWeatherTarget) {
      body.allow_hide_auto_weather = this.createAllowHideAutoWeatherTarget.checked
    }
    if (this.hasCreateShowInDesignTarget) {
      body.show_in_design = this.createShowInDesignTarget.checked
      body.show_in_construction = this.createShowInConstructionTarget.checked
    }
    if (!body.name) return

    const payload = {}
    payload[this.resourceKeyValue] = body

    this._doCreate(this.createUrlValue, payload)
  }

  async _doCreate(url, payload) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(payload)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeCreateModal()
        // Form templates & report types have special columns (links, description) — reload to render server-side
        if (this.resourceKeyValue === "form_template" || this.resourceKeyValue === "daily_report_type") {
          this._reloadWithToast(data.message || "Created successfully")
          return
        }
        this._insertCreatedRow(data)
        this._restripe()
        this._showToast(data.message || "Created successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Edit ──────────────────────────────────────────────────────────

  openEditModal(event) {
    document.dispatchEvent(new CustomEvent("modal:open"))
    const btn = event.currentTarget
    this.editIdTarget.value = btn.dataset.id
    this.editNameTarget.value = btn.dataset.name || ""
    if (this.hasEditSequenceTarget) {
      this.editSequenceTarget.value = btn.dataset.sequence || ""
    }
    if (this.hasEditCodeTarget) {
      this.editCodeTarget.value = btn.dataset.code || ""
    }
    if (this.hasEditDescriptionTarget) {
      this.editDescriptionTarget.value = btn.dataset.description || ""
    }
    if (this.hasEditFormTemplateIdTarget) {
      this.editFormTemplateIdTarget.value = btn.dataset.formTemplateId || ""
    }
    if (this.hasEditAllowHideAutoWeatherTarget) {
      this.editAllowHideAutoWeatherTarget.checked = btn.dataset.allowHideAutoWeather === "true"
    }
    if (this.hasEditShowInDesignTarget) {
      this.editShowInDesignTarget.checked = btn.dataset.showInDesign === "true"
      this.editShowInConstructionTarget.checked = btn.dataset.showInConstruction === "true"
    }
    this.editModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  submitUpdate() {
    const id = this.editIdTarget.value
    const body = { name: this.editNameTarget.value.trim() }
    if (this.hasEditSequenceTarget && this.editSequenceTarget.value) {
      body.sequence = this.editSequenceTarget.value
    }
    if (this.hasEditCodeTarget && this.editCodeTarget.value) {
      body.code = this.editCodeTarget.value
    }
    if (this.hasEditDescriptionTarget) {
      body.description = this.editDescriptionTarget.value.trim()
    }
    if (this.hasEditFormTemplateIdTarget) {
      body.form_template_id = this.editFormTemplateIdTarget.value || null
    }
    if (this.hasEditAllowHideAutoWeatherTarget) {
      body.allow_hide_auto_weather = this.editAllowHideAutoWeatherTarget.checked
    }
    if (this.hasEditShowInDesignTarget) {
      body.show_in_design = this.editShowInDesignTarget.checked
      body.show_in_construction = this.editShowInConstructionTarget.checked
    }
    if (!body.name) return

    const payload = {}
    payload[this.resourceKeyValue] = body

    this._doUpdate(`${this.baseUrlValue}/${id}`, payload, id)
  }

  async _doUpdate(url, payload, id) {
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify(payload)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeEditModal()
        // Form templates & report types have link elements in cells — reload to re-render
        if (this.resourceKeyValue === "form_template" || this.resourceKeyValue === "daily_report_type") {
          this._reloadWithToast(data.message || "Updated successfully")
          return
        }
        const row = this.element.querySelector(`tbody tr[data-id="${id}"]`)
        if (row) {
          const name = data.name || payload[this.resourceKeyValue]?.name
          const cells = row.querySelectorAll("td")
          const key = this.resourceKeyValue

          if (key === "daily_report_type") {
            // columns: drag-handle | sequence | name | actions
            if (data.sequence !== undefined && cells[1]) {
              const badge = cells[1].querySelector("span")
              if (badge) badge.textContent = data.sequence
            }
            if (cells[2]) {
              // Preserve the "Default" badge if present
              const defaultBadge = cells[2].querySelector("span")
              cells[2].childNodes.forEach(n => { if (n.nodeType === Node.TEXT_NODE) n.remove() })
              cells[2].insertBefore(document.createTextNode(name + " "), defaultBadge || null)
            }
          } else if (key === "activity_location") {
            // columns: code | name | actions
            if (data.code !== undefined && cells[0]) cells[0].textContent = data.code
            if (cells[1]) cells[1].textContent = name
          } else {
            // columns: name | (optional count) | actions — first cell is name
            if (cells[0]) cells[0].textContent = name
          }

          const editBtn = row.querySelector('[data-action*="openEditModal"]')
          if (editBtn) {
            editBtn.dataset.name = name
            if (data.sequence !== undefined) editBtn.dataset.sequence = data.sequence
            if (data.code !== undefined) editBtn.dataset.code = data.code
          }
        }
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Delete ────────────────────────────────────────────────────────

  openDeleteConfirm(event) {
    document.dispatchEvent(new CustomEvent("modal:open"))
    this.deleteIdTarget.value = event.currentTarget.dataset.id
    this.deleteConfirmModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeDeleteConfirm() {
    this.deleteConfirmModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  submitDelete() {
    const id = this.deleteIdTarget.value
    this._doDelete(`${this.baseUrlValue}/${id}`, id)
  }

  async _doDelete(url, id) {
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeDeleteConfirm()
        const row = this.element.querySelector(`tbody tr[data-id="${id}"]`)
        if (row) row.remove()
        this.element.querySelectorAll(`tbody tr[data-parent-id="${id}"]`).forEach(r => r.remove())
        this._restripe()
        this._showToast(data.message || "Deleted successfully")
      } else {
        this.closeDeleteConfirm()
        this._showToast(data.message || "Cannot delete", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Toggle (e.g. multiple ball in court) ──────────────────────────

  async toggle(event) {
    if (!this.hasToggleUrlValue) return
    try {
      const response = await fetch(this.toggleUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Set as Default (DailyReportTypes) ───────────────────────────

  async setDefaultReportType(event) {
    if (!this.hasSetDefaultUrlValue) return
    const id = event.currentTarget.dataset.id
    try {
      const response = await fetch(`${this.setDefaultUrlValue}/${id}`, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._reloadWithToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Toggle edit number ────────────────────────────────────────────

  async toggleEditNumber() {
    if (!this.hasToggleEditNumberUrlValue) return
    try {
      const response = await fetch(this.toggleEditNumberUrlValue, {
        method: "PATCH",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Updated successfully")
      } else {
        this._showToast(data.errors?.join(", ") || data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── DOM helpers ───────────────────────────────────────────────────

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;")
  }

  _hasCountColumn() {
    return this.element.querySelectorAll("thead th").length >= 3
  }

  _buildRow(id, name, { count = 0, isSubcategory = false, parentId = null } = {}) {
    const hasCount = this._hasCountColumn()
    const escapedName = this._esc(name)

    const nameTdClass = isSubcategory
      ? "px-3 py-1.5 text-[13px] text-gray-600 pl-8"
      : "px-3 py-1.5 text-[13px] text-gray-900 font-medium"

    const countTd = hasCount
      ? `<td class="px-3 py-1.5 text-[13px] text-gray-500 text-right">${count}</td>`
      : ""

    const attrs = parentId
      ? `data-id="${id}" data-parent-id="${parentId}"`
      : `data-id="${id}"`

    return `<tr ${attrs} class="bg-white border-b border-gray-100 hover:bg-brand/[0.04]">
      <td class="${nameTdClass}">${escapedName}</td>
      ${countTd}
      <td class="px-3 py-1.5 text-right">
        <div class="relative" data-controller="dropdown">
          <button type="button" class="p-1 rounded hover:bg-gray-100 cursor-pointer" data-action="click->dropdown#toggle">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>
          </button>
          <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-32" data-dropdown-target="menu">
            <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer" data-action="click->cpanel-construction#openEditModal" data-id="${id}" data-name="${escapedName}">Edit</button>
            <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer" data-action="click->cpanel-construction#openDeleteConfirm" data-id="${id}">Remove</button>
          </div>
        </div>
      </td>
    </tr>`
  }

  _insertCreatedRow(data) {
    const tbody = this.element.querySelector("tbody")
    if (!tbody) return

    const key = this.resourceKeyValue
    let html

    if (key === "daily_report_type") {
      const escapedName = this._esc(data.name)
      const seq = data.sequence || tbody.querySelectorAll("tr").length + 1
      html = `<tr data-id="${data.id}" class="bg-white border-b border-gray-100 hover:bg-brand/[0.04]">
        <td class="px-1 py-1.5 w-8">
          <div class="drag-handle cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/></svg>
          </div>
        </td>
        <td class="px-3 py-1.5">
          <span class="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[13px] leading-none bg-gray-200 text-text-muted">${seq}</span>
        </td>
        <td class="px-3 py-1.5 text-gray-900 font-medium">${escapedName}</td>
        <td class="px-3 py-1.5 text-right">
          <div class="relative" data-controller="dropdown">
            <button type="button" class="p-1 rounded hover:bg-gray-100 cursor-pointer" data-action="click->dropdown#toggle">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-600"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>
            </button>
            <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg py-1 w-40" data-dropdown-target="menu">
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer" data-action="click->cpanel-construction#openEditModal" data-id="${data.id}" data-name="${escapedName}" data-sequence="${seq}">Edit</button>
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer" data-action="click->cpanel-construction#setDefaultReportType" data-id="${data.id}">Set as default</button>
              <button type="button" class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer" data-action="click->cpanel-construction#openDeleteConfirm" data-id="${data.id}">Remove</button>
            </div>
          </div>
        </td>
      </tr>`
      tbody.insertAdjacentHTML("beforeend", html)
      return
    }

    const isSubcategory = !!data.parent_id
    html = this._buildRow(data.id, data.name, {
      count: 0,
      isSubcategory,
      parentId: data.parent_id
    })

    if (isSubcategory) {
      const subcategoryRows = tbody.querySelectorAll(`tr[data-parent-id="${data.parent_id}"]`)
      if (subcategoryRows.length > 0) {
        subcategoryRows[subcategoryRows.length - 1].insertAdjacentHTML("afterend", html)
      } else {
        const parentRow = tbody.querySelector(`tr[data-id="${data.parent_id}"]`)
        if (parentRow) {
          parentRow.insertAdjacentHTML("afterend", html)
        } else {
          tbody.insertAdjacentHTML("beforeend", html)
        }
      }
    } else {
      tbody.insertAdjacentHTML("beforeend", html)
    }
  }

  _restripe() {
    const rows = this.element.querySelectorAll("tbody tr")
    let masterIndex = 0
    rows.forEach(row => {
      const isSubcategory = row.hasAttribute("data-parent-id")
      row.classList.remove("bg-white", "bg-gray-50/60")
      if (isSubcategory) {
        row.classList.add("bg-white")
      } else {
        row.classList.add(masterIndex % 2 ? "bg-gray-50/60" : "bg-white")
        masterIndex++
      }
    })
  }
}
